/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    getMediaLocationsForProductAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric,
    MediaLocationsInput
} from '@msdyn365-commerce/core';
import { AsyncResult, MediaLocation, ProductsDataActions, SimpleProduct } from '@msdyn365-commerce/retail-proxy';

import { getMediaLocationsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    QueryResultSettingsProxy,
    generateImageUrl,
    getSelectedProductIdFromActionInput,
    getSelectedVariant,
    SelectedVariantInput,
    IDimensionForSelectedVariant,
    ArrayExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import getAllDimensionsForProductActionDataAction, { GetAllDimensionsForProductInput } from './get-all-dimensions-for-product';

/**
 * The action input class for the getMediaLocationsForSelectedVariant data action.
 */
export class MediaLocationsForSelectedVariantInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public catalogId: number;

    public selectedProduct: SimpleProduct | undefined;

    constructor(productId: number, channelId: number, selectedProduct?: SimpleProduct, catalogId?: number) {
        this.productId = productId;
        this.channelId = channelId;
        this.catalogId = catalogId || 0;
        this.selectedProduct = selectedProduct;
    }

    public getCacheKey = () => 'MediaLocationsForSelectedVariant';

    public getCacheObjectType = () => 'MediaLocations';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * The createInput method for the getMediaLocationsForSelectedVariant data action.
 * @param inputData The input data passed to the createInput method.
 */
export const createMediaLocationsForSelectedVariantInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): MediaLocationsForSelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);
    const catalogId = getCatalogId(inputData.requestContext);

    if (productId) {
        return new MediaLocationsForSelectedVariantInput(+productId, +inputData.requestContext.apiSettings.channelId, undefined, catalogId);
    }
    throw new Error('Unable to create MediaLocationsForSelectedVariantInput, no productId found on module config or query');
};

/**
 * The action method for the getMediaLocationsForSelectedVariant data action.
 * @param input
 * @param ctx
 */
export async function getMediaLocationsForSelectedVariantAction(
    input: MediaLocationsForSelectedVariantInput,
    ctx: IActionContext
): Promise<MediaLocation[]> {
    return AsyncResult.resolve()
        .then(() => {
            const activeProduct: SimpleProduct | undefined = input.selectedProduct;

            if (!activeProduct) {
                const selectedVariantInput = new SelectedVariantInput(
                    input.productId,
                    input.channelId,
                    undefined,
                    undefined,
                    ctx.requestContext
                );
                return getSelectedVariant(selectedVariantInput, ctx);
            }

            return activeProduct;
        })
        .then(productResult => {
            const activeProduct = productResult;

            const newProductRecId = activeProduct?.ExtensionProperties?.find(ep => ep.Key === 'NewSku')?.Value?.StringValue?.split(':')[0];
            if (activeProduct && newProductRecId && +newProductRecId) {
                process.env.NODE_ENV === 'development' && console.log('get-media-locations-for-selected-variant', newProductRecId);
                const getAllDimensionsForProductInput = new GetAllDimensionsForProductInput(+newProductRecId, input.channelId);
                return getAllDimensionsForProductActionDataAction(getAllDimensionsForProductInput, ctx).then(
                    (dimensions: IDimensionForSelectedVariant[]) => {
                        const validProducts: Record<number, Record<number, string>> = {};

                        dimensions.forEach(d => {
                            d.dimensionValuesWithInventory?.forEach(inv => {
                                inv.ProductIds?.forEach(variantId => {
                                    const variantDims = validProducts[variantId] ?? (validProducts[variantId] = {});
                                    variantDims[d.DimensionTypeValue] = inv.DimensionValue?.Value ?? '';
                                });
                            });
                        });

                        var variantId = ArrayExtensions.firstOrDefault(
                            Object.keys(validProducts)
                                .filter(variantId => Object.keys(validProducts[variantId]).length === dimensions.length)
                                .filter(variantId => {
                                    const mismatched = activeProduct.Dimensions?.filter(d => d.DimensionTypeValue !== 4).find(
                                        d =>
                                            (d.DimensionValue?.Value?.toLowerCase() ?? '') !==
                                            (validProducts[variantId][d.DimensionTypeValue] ?? '').toLowerCase()
                                    );

                                    return !mismatched;
                                })
                        );

                        // process.env.NODE_ENV === 'development' &&
                        //     console.log('get-media-locations-for-selected-variant', variantId, activeProduct.Dimensions, validProducts);
                        if (variantId && +variantId) {
                            return ProductsDataActions.getByIdAsync({ callerContext: ctx }, +variantId, input.channelId).then(product => {
                                // process.env.NODE_ENV === 'development' &&
                                //     console.log('get-media-locations-for-selected-variant', JSON.parse(JSON.stringify(product)));
                                return product;
                            });
                        }

                        return activeProduct;
                    }
                );
            }

            return activeProduct;
        })
        .then(productResult => {
            const activeProduct = productResult;

            if (activeProduct) {
                if (ctx.requestContext.app?.config?.OmniChannelMedia) {
                    const mediaLocationsInput = new MediaLocationsInput(
                        ctx.requestContext,
                        activeProduct.RecordId,
                        input.channelId,
                        input.catalogId
                    );
                    return getMediaLocationsForProductAction(mediaLocationsInput, ctx);
                }
                return getMediaLocationsAsync(
                    { callerContext: ctx, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx) },
                    activeProduct.RecordId,
                    input.channelId,
                    input.catalogId
                ).then(response => {
                    return response.map(
                        (mediaLocation: MediaLocation): MediaLocation => {
                            mediaLocation.Uri = generateImageUrl(mediaLocation.Uri, ctx.requestContext.apiSettings);
                            mediaLocation.AltText = mediaLocation.AltText ? mediaLocation.AltText : activeProduct.Name;
                            return mediaLocation;
                        }
                    );
                });
            }

            return [];
        })
        .catch(error => {
            ctx.trace(error);
            ctx.telemetry.error(error.message);
            ctx.telemetry.debug('[getMediaLocationsForSelectedVariantAction]Error executing action');
            throw new Error('[getMediaLocationsForSelectedVariantAction]Error executing action');
        });
}

/**
 * The getMediaLocationsForSelectedVariant data action
 * Gets the currently selected variant for the page via the getSelectedVariant data action,
 * and then gets the media location information for the variant via the MediaLocations RetailServer API.
 */
export const getMediaLocationsForSelectedVariantActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-media-locations-for-selected-variant',
    action: <IAction<MediaLocation[] | null>>getMediaLocationsForSelectedVariantAction,
    input: createMediaLocationsForSelectedVariantInput
});

export default getMediaLocationsForSelectedVariantActionDataAction;
